import { Fragment, type ReactNode, useEffect, useState } from 'react'

import { t } from '../../../../../common/i18n.js'
import { Language } from '../../../../../common/types/i18n.js'
import {
    ERROR_LOG_SEVERITIES,
    ERROR_LOG_SOURCES,
} from '../../../../../common/types/portal-error-log.js'
import { Select } from '../../../../components/forms/select/select.js'
import { TextField } from '../../../../components/forms/text-field/text-field.js'
import {
    Activity,
    AlertTriangle,
    ChevronLeft,
    ChevronRight,
    Globe,
    TimerReset,
    X,
} from '../../../../components/icon/solar-icons.js'
import { PageHeader } from '../../../../components/page-header/page-header.js'
import { PageShell } from '../../../../components/page-shell/page-shell.js'
import { Spinner } from '../../../../components/spinner/spinner.js'
import { portalGet } from '../../utils/api.js'
import { formatUtcTimestamp } from '../../utils/format.js'
import './error-log-view.css'

interface ErrorLogEntry {
    id: number
    timestamp_utc: string
    severity: string
    source: string
    error_name: string
    message: string
    stack: string | null
    http_method: string | null
    http_path: string | null
    http_status: number | null
    job_name: string | null
    user_id: number | null
    ip_address: string | null
    user_agent: string | null
    metadata: Record<string, unknown> | null
}

export interface ErrorLogViewProps {
    lang: Language
    // When true, render content only (no PageShell/PageHeader) — the host view
    // (the Settings tab) already provides the page chrome.
    embedded?: boolean
}

const PAGE_SIZE = 50

const SEVERITY_CLASS: Record<string, string> = {
    fatal: 'fatal',
    error: 'error',
    warning: 'warning',
}

const SOURCE_ICONS: Record<string, ReactNode> = {
    http: <Globe size={12} />,
    cron: <TimerReset size={12} />,
    process: <Activity size={12} />,
}

// HTTP errors show "METHOD /path"; cron / process errors show the job name.
const locationText = (entry: ErrorLogEntry): string => {
    if (entry.source === 'http') {
        const method = entry.http_method ? `${entry.http_method} ` : ''
        return `${method}${entry.http_path ?? '—'}`
    }
    return entry.job_name ?? '—'
}

export const ErrorLogView = ({ lang, embedded }: ErrorLogViewProps) => {
    const [entries, setEntries] = useState<ErrorLogEntry[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [isLoading, setIsLoading] = useState(true)

    // Filters — severity/source are fixed enums, so the dropdowns are static
    // (no need for the audit view's "distinct values from recent rows" loader).
    const [filterSeverity, setFilterSeverity] = useState('')
    const [filterSource, setFilterSource] = useState('')
    const [filterSearch, setFilterSearch] = useState('')
    const [filterFromDate, setFilterFromDate] = useState('')
    const [filterToDate, setFilterToDate] = useState('')

    const [expandedId, setExpandedId] = useState<number | null>(null)

    const tE = t.portal.errorLog

    useEffect(() => {
        const load = async () => {
            setIsLoading(true)
            const params = new URLSearchParams()
            params.set('page', String(page))
            params.set('limit', String(PAGE_SIZE))
            if (filterSeverity) params.set('severity', filterSeverity)
            if (filterSource) params.set('source', filterSource)
            if (filterSearch) params.set('search', filterSearch)
            if (filterFromDate) params.set('from_date', filterFromDate)
            if (filterToDate) params.set('to_date', filterToDate)

            const result = await portalGet<{
                entries: ErrorLogEntry[]
                total: number
                page: number
            }>(`/api/portal/error-logs?${params.toString()}`)

            if (result.ok) {
                setEntries(result.data.entries)
                setTotal(result.data.total)
            }
            setIsLoading(false)
        }

        void load()
    }, [page, filterSeverity, filterSource, filterSearch, filterFromDate, filterToDate])

    const totalPages = Math.ceil(total / PAGE_SIZE)
    const showingFrom = total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0
    const showingTo = Math.min(page * PAGE_SIZE, total)

    const handleResetFilters = () => {
        setFilterSeverity('')
        setFilterSource('')
        setFilterSearch('')
        setFilterFromDate('')
        setFilterToDate('')
        setPage(1)
    }

    const hasActiveFilters =
        filterSeverity !== '' ||
        filterSource !== '' ||
        filterSearch !== '' ||
        filterFromDate !== '' ||
        filterToDate !== ''

    if (isLoading && entries.length === 0) {
        return (
            <div className="portal-error__loading">
                <Spinner />
            </div>
        )
    }

    const totalBadge = (
        <span className="portal-error__total-badge">
            {total} {tE.entries(lang)}
        </span>
    )

    const content = (
        <>
            {/* Filters */}
            <div className="portal-error__filters">
                <Select
                    className="portal-error__filter-select"
                    size="small"
                    placeholder={tE.allSeverities(lang)}
                    value={filterSeverity}
                    onChange={(value) => {
                        setFilterSeverity(value)
                        setPage(1)
                    }}
                    options={ERROR_LOG_SEVERITIES.map((s) => ({
                        value: s,
                        label: tE.severities[s]?.(lang) ?? s,
                    }))}
                />

                <Select
                    className="portal-error__filter-select"
                    size="small"
                    placeholder={tE.allSources(lang)}
                    value={filterSource}
                    onChange={(value) => {
                        setFilterSource(value)
                        setPage(1)
                    }}
                    options={ERROR_LOG_SOURCES.map((s) => ({
                        value: s,
                        label: tE.sources[s]?.(lang) ?? s,
                    }))}
                />

                <TextField
                    className="portal-error__filter-input"
                    size="small"
                    value={filterSearch}
                    onChange={(value) => {
                        setFilterSearch(value)
                        setPage(1)
                    }}
                    inputProps={{ type: 'text', placeholder: tE.searchMessage(lang) }}
                />

                <TextField
                    className="portal-error__filter-input"
                    size="small"
                    value={filterFromDate}
                    onChange={(value) => {
                        setFilterFromDate(value)
                        setPage(1)
                    }}
                    inputProps={{ type: 'date', title: tE.fromDate(lang) }}
                />

                <TextField
                    className="portal-error__filter-input"
                    size="small"
                    value={filterToDate}
                    onChange={(value) => {
                        setFilterToDate(value)
                        setPage(1)
                    }}
                    inputProps={{ type: 'date', title: tE.toDate(lang) }}
                />

                {hasActiveFilters && (
                    <button
                        type="button"
                        className="portal-error__filter-reset"
                        onClick={handleResetFilters}
                    >
                        <X size={14} /> {tE.clear(lang)}
                    </button>
                )}
            </div>

            {/* Table */}
            {entries.length === 0 ? (
                <div className="portal-error__empty">
                    <div className="portal-error__empty-icon">
                        <AlertTriangle size={32} />
                    </div>
                    <div>{tE.noEntries(lang)}</div>
                </div>
            ) : (
                <>
                    <div className="portal-error__table-wrap">
                        <table className="portal-error__table">
                            <thead>
                                <tr>
                                    <th>{tE.time(lang)}</th>
                                    <th>{tE.severity(lang)}</th>
                                    <th>{tE.source(lang)}</th>
                                    <th>{tE.error(lang)}</th>
                                    <th>{tE.location(lang)}</th>
                                    <th>{tE.details(lang)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry) => {
                                    const severityClass = SEVERITY_CLASS[entry.severity] ?? 'error'
                                    const isExpanded = expandedId === entry.id

                                    return (
                                        <Fragment key={entry.id}>
                                            <tr
                                                className={
                                                    isExpanded
                                                        ? 'portal-error__row is-expanded'
                                                        : 'portal-error__row'
                                                }
                                                onClick={() =>
                                                    setExpandedId(isExpanded ? null : entry.id)
                                                }
                                            >
                                                <td className="portal-error__date">
                                                    {formatUtcTimestamp(entry.timestamp_utc, lang)}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`portal-error__severity-badge portal-error__severity-badge--${severityClass}`}
                                                    >
                                                        {tE.severities[entry.severity]?.(lang) ??
                                                            entry.severity}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="portal-error__source">
                                                        {SOURCE_ICONS[entry.source]}{' '}
                                                        {tE.sources[entry.source]?.(lang) ??
                                                            entry.source}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="portal-error__error-name">
                                                        {entry.error_name}
                                                    </span>
                                                    <span className="portal-error__error-message">
                                                        {entry.message}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="portal-error__location">
                                                        {locationText(entry)}
                                                    </span>
                                                    {entry.http_status ? (
                                                        <span className="portal-error__status">
                                                            {entry.http_status}
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="portal-error__expand-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            setExpandedId(
                                                                isExpanded ? null : entry.id,
                                                            )
                                                        }}
                                                    >
                                                        {isExpanded ? '▼' : '▶'}
                                                    </button>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="portal-error__detail-row">
                                                    <td colSpan={6}>
                                                        <div className="portal-error__detail-panel">
                                                            <div className="portal-error__detail-grid">
                                                                {entry.user_id !== null && (
                                                                    <div className="portal-error__detail-item">
                                                                        <span className="portal-error__detail-label">
                                                                            {tE.user(lang)}
                                                                        </span>
                                                                        <span className="portal-error__detail-value">
                                                                            #{entry.user_id}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {entry.ip_address && (
                                                                    <div className="portal-error__detail-item">
                                                                        <span className="portal-error__detail-label">
                                                                            IP
                                                                        </span>
                                                                        <span className="portal-error__detail-value portal-error__location">
                                                                            {entry.ip_address}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {entry.user_agent && (
                                                                    <div className="portal-error__detail-item portal-error__detail-item--full">
                                                                        <span className="portal-error__detail-label">
                                                                            {tE.userAgent(lang)}
                                                                        </span>
                                                                        <span className="portal-error__detail-value portal-error__detail-value--ua">
                                                                            {entry.user_agent}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {entry.stack && (
                                                                    <div className="portal-error__detail-item portal-error__detail-item--full">
                                                                        <span className="portal-error__detail-label">
                                                                            {tE.stackTrace(lang)}
                                                                        </span>
                                                                        <pre className="portal-error__detail-stack">
                                                                            {entry.stack}
                                                                        </pre>
                                                                    </div>
                                                                )}
                                                                {entry.metadata && (
                                                                    <div className="portal-error__detail-item portal-error__detail-item--full">
                                                                        <span className="portal-error__detail-label">
                                                                            {tE.metadata(lang)}
                                                                        </span>
                                                                        <pre className="portal-error__detail-json">
                                                                            {JSON.stringify(
                                                                                entry.metadata,
                                                                                null,
                                                                                2,
                                                                            )}
                                                                        </pre>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="portal-error__pagination">
                            <div className="portal-error__pagination-info">
                                {tE.showing(showingFrom, showingTo, total)(lang)}
                            </div>
                            <div className="portal-error__pagination-controls">
                                <button
                                    type="button"
                                    className="portal-error__page-btn"
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                >
                                    <ChevronLeft size={14} />
                                    {tE.prev(lang)}
                                </button>
                                <span className="portal-error__page-label">
                                    {tE.page(page, totalPages)(lang)}
                                </span>
                                <button
                                    type="button"
                                    className="portal-error__page-btn"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                >
                                    {tE.next(lang)}
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    )

    if (embedded) {
        return (
            <>
                <div className="portal-error__embedded-header">{totalBadge}</div>
                {content}
            </>
        )
    }

    return (
        <PageShell>
            <PageHeader title={tE.title(lang)} actions={totalBadge} />
            {content}
        </PageShell>
    )
}
